import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Users, UserPlus, Search, Check, X, Clock, Zap, Crown,
  Heart, ChevronRight, PartyPopper, MessageCircle, Camera,
} from "lucide-react";
import UserLink from "../components/UserLink";
import PhotoFeedCard from "../components/PhotoFeedCard";
import timeAgo from "../utils/timeAgo";
import { T } from "../theme";

const API = process.env.REACT_APP_API_URL || "";


function Avatar({ username, size = 40 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", flexShrink: 0,
      background: "linear-gradient(135deg, #b44fff, #ff3cac)",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.35, fontWeight: 800, color: "#fff",
      border: "2px solid rgba(255,255,255,0.15)",
    }}>
      {username?.slice(0, 2).toUpperCase() || "??"}
    </div>
  );
}

function Label({ children, style }) {
  return (
    <p style={{
      fontSize: "0.72rem", fontWeight: 700, color: T.textMuted,
      textTransform: "uppercase", letterSpacing: "0.1em", margin: 0, ...style,
    }}>
      {children}
    </p>
  );
}

const ACTIVITY_CONFIG = {
  scan:          { icon: Zap,          color: T.cyan,   label: "scanned" },
  claim:         { icon: Crown,        color: T.gold,   label: "claimed" },
  register:      { icon: Heart,        color: T.pink,   label: "registered" },
  transfer:      { icon: ChevronRight, color: T.purple, label: "transferred" },
  friend_added:  { icon: Users,        color: T.green,  label: "" },
  event_checkin: { icon: PartyPopper,  color: T.gold,   label: "checked in at" },
  photo_post:    { icon: Camera,       color: "#d946ef", label: "shared a photo" },
};

function ActivityCard({ activity }) {
  const cfg = ACTIVITY_CONFIG[activity.type] || ACTIVITY_CONFIG.scan;
  const Icon = cfg.icon;

  let text = "";
  if (activity.type === "friend_added") {
    text = activity.message;
  } else if (activity.type === "event_checkin") {
    text = `${cfg.label} ${activity.eventName || "an event"}`;
  } else {
    text = `${cfg.label} ${activity.kandiName || activity.kandiId || "a kandi"}`;
  }
  if (activity.location && activity.type !== "friend_added") {
    text += ` at ${activity.location}`;
  }

  return (
    <div style={{
      display: "flex", gap: 12, padding: "14px 0",
      borderBottom: "1px solid rgba(255,255,255,0.05)",
    }}>
      <Avatar username={activity.user?.username} size={38} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
          {activity.user?.username
            ? <UserLink username={activity.user.username} userId={activity.user.id} style={{ fontSize: "0.9rem" }} />
            : <span style={{ color: T.textPrimary, fontWeight: 700, fontSize: "0.9rem" }}>Unknown</span>}
          <Icon size={13} color={cfg.color} style={{ filter: `drop-shadow(0 0 4px ${cfg.color})` }} />
        </div>
        <p style={{ margin: 0, color: "rgba(255,255,255,0.7)", fontSize: "0.85rem", lineHeight: 1.4 }}>
          {text}
        </p>
        <p style={{ margin: "4px 0 0", color: T.textMuted, fontSize: "0.73rem" }}>
          {timeAgo(activity.createdAt)}
        </p>
      </div>
    </div>
  );
}

function FriendCard({ friend, onRemove }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 14, padding: "14px 16px",
      background: T.card, border: T.border, borderRadius: 16, marginBottom: 8,
    }}>
      <Avatar username={friend.username} size={44} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: 0, fontSize: "0.95rem" }}>
          <UserLink username={friend.username} userId={friend.id} style={{ fontSize: "0.95rem" }} />
        </p>
        <p style={{ margin: "2px 0 0", color: T.textMuted, fontSize: "0.78rem" }}>
          {friend.bio || "PLUR vibes only"}
        </p>
      </div>
      <button
        onClick={() => onRemove(friend.friendshipId)}
        style={{
          background: "rgba(255,68,68,0.12)", border: "1px solid rgba(255,68,68,0.25)",
          borderRadius: 10, padding: "6px 12px", color: "#ff6b6b",
          fontSize: "0.75rem", fontWeight: 600, cursor: "pointer",
        }}
      >
        Remove
      </button>
    </div>
  );
}

function RequestCard({ request, type, onAccept, onReject }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 14, padding: "14px 16px",
      background: T.card, border: T.border, borderRadius: 16, marginBottom: 8,
    }}>
      <Avatar username={request.user.username} size={42} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: 0, fontSize: "0.92rem" }}>
          <UserLink username={request.user.username} userId={request.user.id} style={{ fontSize: "0.92rem" }} />
        </p>
        <p style={{ margin: "2px 0 0", color: T.textMuted, fontSize: "0.75rem" }}>
          {type === "incoming" ? "wants to be friends" : "request sent"} · {timeAgo(request.createdAt)}
        </p>
      </div>
      {type === "incoming" && (
        <div style={{ display: "flex", gap: 6 }}>
          <button
            onClick={() => onAccept(request.friendshipId)}
            style={{
              background: "linear-gradient(135deg, #b44fff, #ff3cac)", border: "none",
              borderRadius: 10, padding: "7px 14px", color: "#fff",
              fontSize: "0.78rem", fontWeight: 700, cursor: "pointer",
            }}
          >
            <Check size={13} style={{ marginRight: 3, verticalAlign: -2 }} /> Accept
          </button>
          <button
            onClick={() => onReject(request.friendshipId)}
            style={{
              background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: 10, padding: "7px 12px", color: T.textMuted,
              fontSize: "0.78rem", fontWeight: 600, cursor: "pointer",
            }}
          >
            <X size={13} style={{ verticalAlign: -2 }} />
          </button>
        </div>
      )}
      {type === "outgoing" && (
        <div style={{ display: "flex", alignItems: "center", gap: 5, color: T.textMuted, fontSize: "0.75rem" }}>
          <Clock size={12} /> Pending
        </div>
      )}
    </div>
  );
}

export default function FriendsPage() {
  const user = JSON.parse(localStorage.getItem("user") || "null");
  const [tab, setTab] = useState("feed");
  const [feed, setFeed] = useState([]);
  const [feedHasMore, setFeedHasMore] = useState(false);
  const [feedCursor, setFeedCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [friends, setFriends] = useState([]);
  const [pending, setPending] = useState({ incoming: [], outgoing: [] });
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [friendIds, setFriendIds] = useState(new Set());

  const fetchFriends = useCallback(async () => {
    if (!user?.id) return;
    try {
      const [friendsRes, pendingRes] = await Promise.all([
        axios.get(`${API}/api/friends/${user.id}`),
        axios.get(`${API}/api/friends/${user.id}/pending`),
      ]);
      setFriends(friendsRes.data.friends || []);
      setPending({ incoming: pendingRes.data.incoming || [], outgoing: pendingRes.data.outgoing || [] });
      setFriendIds(new Set((friendsRes.data.friends || []).map(f => f.id)));
    } catch (err) {
      console.error("Fetch friends error:", err);
    }
  }, [user?.id]);

  const fetchFeed = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await axios.get(`${API}/api/friends/${user.id}/feed?viewerId=${user.id}&limit=20`);
      setFeed(res.data.activities || []);
      setFeedHasMore(!!res.data.hasMore);
      setFeedCursor(res.data.nextCursor || null);
    } catch (err) {
      console.error("Fetch feed error:", err);
    }
  }, [user?.id]);

  const loadMoreFeed = useCallback(async () => {
    if (!user?.id || !feedCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await axios.get(
        `${API}/api/friends/${user.id}/feed?viewerId=${user.id}&limit=20&before=${encodeURIComponent(feedCursor)}`
      );
      setFeed(prev => [...prev, ...(res.data.activities || [])]);
      setFeedHasMore(!!res.data.hasMore);
      setFeedCursor(res.data.nextCursor || null);
    } catch (err) {
      console.error("Load more feed error:", err);
    } finally {
      setLoadingMore(false);
    }
  }, [user?.id, feedCursor, loadingMore]);

  useEffect(() => {
    Promise.all([fetchFriends(), fetchFeed()]).finally(() => setLoading(false));
  }, [fetchFriends, fetchFeed]);

  const handleSearch = async () => {
    if (!searchQuery.trim() || searchQuery.trim().length < 2) return;
    setSearching(true);
    try {
      const res = await axios.get(`${API}/api/users/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchResults((res.data.users || []).filter(u => u.id !== user?.id));
    } catch (err) {
      console.error("Search error:", err);
    } finally {
      setSearching(false);
    }
  };

  const handleSendRequest = async (targetUserId) => {
    try {
      await axios.post(`${API}/api/friends/request`, { userId: user.id, targetUserId });
      await fetchFriends();
      setSearchResults(prev => prev.filter(u => u.id !== targetUserId));
    } catch (err) {
      console.error("Send request error:", err);
    }
  };

  const handleAccept = async (friendshipId) => {
    try {
      await axios.post(`${API}/api/friends/accept/${friendshipId}`, { userId: user.id });
      await Promise.all([fetchFriends(), fetchFeed()]);
    } catch (err) {
      console.error("Accept error:", err);
    }
  };

  const handleReject = async (friendshipId) => {
    try {
      await axios.post(`${API}/api/friends/reject/${friendshipId}`, { userId: user.id });
      await fetchFriends();
    } catch (err) {
      console.error("Reject error:", err);
    }
  };

  const handleRemove = async (friendshipId) => {
    try {
      await axios.delete(`${API}/api/friends/${friendshipId}`, { data: { userId: user.id } });
      await Promise.all([fetchFriends(), fetchFeed()]);
    } catch (err) {
      console.error("Remove error:", err);
    }
  };

  const pendingOutIds = new Set(pending.outgoing.map(p => p.user.id));

  // Client-side safety layer on top of server privacy filtering: restricted
  // events and events whose actor is gone (e.g. unfriended after posting)
  // are skipped silently — no error card, no placeholder.
  const isDev = process.env.NODE_ENV === "development";
  const visibleFeed = feed.filter(a => {
    if (a?.restricted) {
      if (isDev) console.log("[feed] skipping restricted item:", a.id);
      return false;
    }
    if (!a?.user?.username) {
      if (isDev) console.log("[feed] skipping item with missing actor:", a?.id);
      return false;
    }
    return true;
  });

  const tabs = [
    { key: "feed", label: "Feed", icon: MessageCircle, count: 0 },
    { key: "friends", label: "Friends", icon: Users, count: friends.length },
    { key: "requests", label: "Requests", icon: UserPlus, count: pending.incoming.length },
    { key: "search", label: "Find", icon: Search, count: 0 },
  ];

  if (!user) return null;

  return (
    <div style={{
      minHeight: "100vh", background: T.bg, color: T.textPrimary,
      fontFamily: "'Inter', system-ui, sans-serif",
    }}>
      <div className="friends-root" style={{ maxWidth: 620, margin: "0 auto", padding: "80px 16px 24px" }}>

        {/* Header */}
        <div style={{ marginBottom: 20 }}>
          <h1 style={{
            margin: "0 0 4px",
            fontFamily: "var(--font-display)",
            fontSize: "1.6rem", fontWeight: 700,
            color: T.pink,
            textShadow: `0 0 22px ${T.purple}60`,
          }}>
            Friends
          </h1>
          <p style={{ margin: 0, color: T.textMuted, fontSize: "0.85rem" }}>
            Your PLUR fam
          </p>
        </div>

        {/* Tabs */}
        <div className="friends-tabs" style={{
          display: "flex", gap: 6, marginBottom: 18, padding: 4,
          background: "rgba(255,255,255,0.03)", borderRadius: 14,
          border: "1px solid rgba(255,255,255,0.06)",
        }}>
          {tabs.map(t => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                style={{
                  flex: 1, padding: "10px 8px", border: "none", borderRadius: 10,
                  background: active ? "rgba(180,79,255,0.18)" : "transparent",
                  color: active ? T.purple : T.textMuted,
                  fontSize: "0.78rem", fontWeight: 700, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
                  transition: "all 0.2s",
                }}
              >
                <Icon size={14} />
                {t.label}
                {t.count > 0 && (
                  <span style={{
                    background: active ? T.purple : "rgba(255,255,255,0.12)",
                    color: active ? "#fff" : T.textMuted,
                    fontSize: "0.65rem", fontWeight: 800, borderRadius: 8,
                    padding: "1px 6px", minWidth: 18, textAlign: "center",
                  }}>
                    {t.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Feed Tab */}
        {tab === "feed" && (
          <div style={{
            background: T.card, backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
            border: T.border, borderRadius: 24, padding: "20px 20px",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
              <MessageCircle size={14} color={T.purple} style={{ filter: `drop-shadow(0 0 6px ${T.purple})` }} />
              <Label>Activity Feed</Label>
            </div>
            {loading ? (
              <p style={{ color: T.textMuted, textAlign: "center", padding: 30, margin: 0 }}>Loading...</p>
            ) : visibleFeed.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px 20px" }}>
                <Users size={36} color="rgba(255,255,255,0.12)" style={{ marginBottom: 12 }} />
                <p style={{ color: T.textMuted, margin: 0, fontSize: "0.88rem" }}>
                  No activity yet. Add friends to see their kandi adventures here!
                </p>
              </div>
            ) : (
              <>
                {visibleFeed.map(a => a.type === "photo_post"
                  ? <PhotoFeedCard key={a.id} activity={a} apiUrl={API} viewerId={user.id} friends={friends} />
                  : <ActivityCard key={a.id} activity={a} />
                )}

                {loadingMore && (
                  <>
                    <div className="feed-skeleton" style={{ height: 74, marginTop: 10 }} />
                    <div className="feed-skeleton" style={{ height: 74, marginTop: 10 }} />
                  </>
                )}

                {feedHasMore && !loadingMore && (
                  <button
                    onClick={loadMoreFeed}
                    style={{
                      width: "100%", marginTop: 14, padding: "12px 16px",
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(180,79,255,0.3)",
                      borderRadius: 14, color: T.purple,
                      fontSize: "0.82rem", fontWeight: 700, cursor: "pointer",
                    }}
                  >
                    Load more
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {/* Friends List Tab */}
        {tab === "friends" && (
          <div>
            {friends.length === 0 ? (
              <div style={{
                background: T.card, border: T.border, borderRadius: 24,
                padding: "40px 20px", textAlign: "center",
              }}>
                <Users size={36} color="rgba(255,255,255,0.12)" style={{ marginBottom: 12 }} />
                <p style={{ color: T.textMuted, margin: 0, fontSize: "0.88rem" }}>
                  No friends yet — search for people to connect with!
                </p>
              </div>
            ) : (
              friends.map(f => <FriendCard key={f.friendshipId} friend={f} onRemove={handleRemove} />)
            )}
          </div>
        )}

        {/* Requests Tab */}
        {tab === "requests" && (
          <div>
            {pending.incoming.length > 0 && (
              <>
                <Label style={{ marginBottom: 10 }}>Incoming Requests</Label>
                {pending.incoming.map(r => (
                  <RequestCard key={r.friendshipId} request={r} type="incoming" onAccept={handleAccept} onReject={handleReject} />
                ))}
              </>
            )}
            {pending.outgoing.length > 0 && (
              <>
                <Label style={{ margin: "18px 0 10px" }}>Sent Requests</Label>
                {pending.outgoing.map(r => (
                  <RequestCard key={r.friendshipId} request={r} type="outgoing" />
                ))}
              </>
            )}
            {pending.incoming.length === 0 && pending.outgoing.length === 0 && (
              <div style={{
                background: T.card, border: T.border, borderRadius: 24,
                padding: "40px 20px", textAlign: "center",
              }}>
                <UserPlus size={36} color="rgba(255,255,255,0.12)" style={{ marginBottom: 12 }} />
                <p style={{ color: T.textMuted, margin: 0, fontSize: "0.88rem" }}>
                  No pending requests
                </p>
              </div>
            )}
          </div>
        )}

        {/* Search Tab */}
        {tab === "search" && (
          <div>
            <div style={{
              display: "flex", gap: 8, marginBottom: 16,
            }}>
              <input
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleSearch()}
                placeholder="Search by username..."
                style={{
                  flex: 1, padding: "12px 16px",
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: 14, color: T.textPrimary, fontSize: "0.9rem",
                  outline: "none",
                }}
              />
              <button
                onClick={handleSearch}
                disabled={searching}
                style={{
                  padding: "12px 20px", border: "none", borderRadius: 14,
                  background: "linear-gradient(135deg, #b44fff, #ff3cac)",
                  color: "#fff", fontWeight: 700, fontSize: "0.85rem", cursor: "pointer",
                  opacity: searching ? 0.6 : 1,
                }}
              >
                <Search size={16} />
              </button>
            </div>

            {searchResults.map(u => {
              const isFriend = friendIds.has(u.id);
              const isPending = pendingOutIds.has(u.id);
              return (
                <div key={u.id} style={{
                  display: "flex", alignItems: "center", gap: 14, padding: "14px 16px",
                  background: T.card, border: T.border, borderRadius: 16, marginBottom: 8,
                }}>
                  <Avatar username={u.username} size={42} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: "0.92rem" }}>
                      <UserLink username={u.username} userId={u.id} style={{ fontSize: "0.92rem" }} />
                    </p>
                    <p style={{ margin: "2px 0 0", color: T.textMuted, fontSize: "0.75rem" }}>
                      {u.bio || "PLUR vibes only"}
                    </p>
                  </div>
                  {isFriend ? (
                    <span style={{ color: T.green, fontSize: "0.75rem", fontWeight: 700 }}>
                      <Check size={13} style={{ verticalAlign: -2 }} /> Friends
                    </span>
                  ) : isPending ? (
                    <span style={{ color: T.textMuted, fontSize: "0.75rem", fontWeight: 600 }}>
                      <Clock size={12} style={{ verticalAlign: -2 }} /> Pending
                    </span>
                  ) : (
                    <button
                      onClick={() => handleSendRequest(u.id)}
                      style={{
                        background: "linear-gradient(135deg, #b44fff, #ff3cac)", border: "none",
                        borderRadius: 10, padding: "7px 14px", color: "#fff",
                        fontSize: "0.78rem", fontWeight: 700, cursor: "pointer",
                      }}
                    >
                      <UserPlus size={13} style={{ marginRight: 3, verticalAlign: -2 }} /> Add
                    </button>
                  )}
                </div>
              );
            })}

            {searchResults.length === 0 && searchQuery && !searching && (
              <div style={{
                background: T.card, border: T.border, borderRadius: 24,
                padding: "40px 20px", textAlign: "center",
              }}>
                <Search size={36} color="rgba(255,255,255,0.12)" style={{ marginBottom: 12 }} />
                <p style={{ color: T.textMuted, margin: 0, fontSize: "0.88rem" }}>
                  No users found. Try a different search.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
